import { Link } from "react-router-dom";
import bizLogo from "../../Assets/biz_logo.svg";

export type BizcoverIndiaBrandMarkProps = {
  /** `react-router` destination when using `Link`. */
  to?: string;
  /** Plain URL for a native `<a>` (e.g. dashboard `Navbar` with `navItems.logo.href`). */
  href?: string;
  variant?: "header" | "footer" | "auth" | "hero" | "marketing";
  className?: string;
};

/**
 * Site wordmark: `biz_logo.svg` + “BizcoverIndia.com” (Alibaba-style orange brand + slightly deeper orange on `.com`).
 */
export function BizcoverIndiaBrandMark({
  to,
  href,
  variant = "header",
  className = "",
}: BizcoverIndiaBrandMarkProps) {
  const imgWrapClass =
    variant === "footer"
      ? "bz-logo-mark-img inline-flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white/95 shadow-sm ring-1 ring-white/25"
      : variant === "hero"
        ? "bz-logo-mark-img inline-flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white/95 shadow-sm ring-1 ring-white/30"
        : variant === "marketing"
          ? "bz-logo-mark-img inline-flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white shadow-[0_1px_3px_rgba(15,23,42,0.08)] ring-1 ring-[#e8ecf0]"
          : "bz-logo-mark-img inline-flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white shadow-[0_1px_3px_rgba(15,23,42,0.08)] ring-1 ring-[#e8ecf0] sm:h-10 sm:w-10";

  const wordRowClass =
    variant === "footer"
      ? "inline-flex min-w-0 items-baseline gap-0 text-[22px] font-bold leading-tight sm:text-[24px]"
      : variant === "hero"
        ? "inline-flex min-w-0 items-baseline gap-0 text-lg font-bold leading-tight sm:text-xl"
        : variant === "marketing"
          ? "inline-flex min-w-0 items-baseline gap-0 text-3xl font-bold leading-tight"
          : variant === "auth"
            ? "inline-flex min-w-0 items-baseline gap-0 text-lg font-bold leading-tight md:text-xl"
            : "inline-flex min-w-0 items-baseline gap-0 text-[17px] font-bold leading-snug sm:text-[19px] sm:leading-[26px]";

  const primaryTextClass =
    variant === "footer"
      ? "min-w-0 truncate text-[#FDBA74]"
      : variant === "hero"
        ? "min-w-0 truncate text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.25)]"
        : "min-w-0 truncate text-[#F97316]";

  const tldClass =
    variant === "footer"
      ? "shrink-0 text-[#F97316]"
      : variant === "hero"
        ? "shrink-0 text-orange-100 drop-shadow-[0_1px_2px_rgba(0,0,0,0.2)]"
        : "shrink-0 text-[#EA580C]";

  const wrapClass =
    variant === "header"
      ? `bz-logo-link relative flex min-h-[48px] min-w-0 max-w-[min(100%,360px)] shrink items-center gap-2 overflow-hidden sm:min-h-[52px] sm:gap-2.5 ${className}`.trim()
      : variant === "footer"
        ? `relative flex min-h-[40px] min-w-0 max-w-[min(100%,360px)] items-center gap-2.5 overflow-hidden ${className}`.trim()
        : variant === "hero"
          ? `flex min-w-0 items-center gap-2 ${className}`.trim()
          : variant === "marketing"
            ? `flex min-w-0 items-center gap-3 ${className}`.trim()
            : `flex min-w-0 items-center gap-2.5 ${className}`.trim();

  const inner = (
    <>
      <span className={imgWrapClass}>
        <img
          src={bizLogo}
          alt=""
          className="h-full w-full object-contain"
          width={48}
          height={48}
          decoding="async"
          aria-hidden
        />
      </span>
      <span className={wordRowClass}>
        <span className={primaryTextClass}>BizcoverIndia</span>
        <span className={tldClass}>.com</span>
      </span>
    </>
  );

  if (href) {
    return (
      <a href={href} className={wrapClass} aria-label="BizcoverIndia.com home">
        {inner}
      </a>
    );
  }

  if (to) {
    return (
      <Link to={to} className={wrapClass} aria-label="BizcoverIndia.com home">
        {inner}
      </Link>
    );
  }

  return (
    <div className={wrapClass} role="img" aria-label="BizcoverIndia.com">
      {inner}
    </div>
  );
}
